/**
 * Study Documents Page
 */

import { useState } from 'react';
import { StudyLayout } from '../../components/study/StudyLayout';
import { Container } from '../../components/ui/Container';
import { UploadZone } from '../../components/study/UploadZone';
import { api } from '../../config/firebase';
import { useStudyStore } from '../../stores/studyStore';

export function StudyDocumentsPage() {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const { documents, addDocument } = useStudyStore();

  const handleFileSelect = async (file: File) => {
    try {
      setIsUploading(true);
      setError(null);
      setUploadProgress(0);

      // Upload document
      const response = await api.study.uploadDocument(file, {
        title: file.name.replace(/\.[^/.]+$/, ''), // Remove extension
      });

      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to upload document');
      }

      if (response.data?.documentId) {
        // Fetch the uploaded document to add to store
        const docResponse = await api.study.getDocument(response.data.documentId);
        if (docResponse.success && docResponse.data) {
          addDocument(docResponse.data);
        }
      }

      setUploadProgress(100);
      // Reset after a moment
      setTimeout(() => {
        setIsUploading(false);
        setUploadProgress(0);
      }, 1000);
    } catch (err: any) {
      console.error('Upload error:', err);
      setError(err.message || 'Failed to upload document');
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  return (
    <StudyLayout>
      <div className="p-6">
        <Container>
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-textPrimary dark:text-[#CCCCCC] mb-2">
              Documents
            </h1>
            <p className="text-textSecondary dark:text-[#858585]">
              Upload and manage your study materials
            </p>
          </div>

          {/* Upload Zone */}
          <div className="mb-8">
            <UploadZone
              onFileSelect={handleFileSelect}
              disabled={isUploading}
            />

            {/* Upload Progress */}
            {isUploading && (
              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-textSecondary dark:text-[#858585]">
                    Uploading...
                  </span>
                  <span className="text-sm text-textSecondary dark:text-[#858585]">
                    {uploadProgress}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div
                    className="bg-orange-600 dark:bg-orange-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}
          </div>

          {/* Documents List */}
          {documents.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {documents.map((doc) => (
                <div
                  key={doc.id}
                  onClick={() => window.location.href = `/study/app/documents/${doc.id}`}
                  className="bg-backgroundSecondary dark:bg-[#252526] rounded-lg border border-gray-200 dark:border-gray-700 p-4 hover:border-orange-500/50 transition-colors cursor-pointer"
                >
                  <h3 className="font-semibold text-textPrimary dark:text-[#CCCCCC] mb-2 truncate">
                    {doc.title}
                  </h3>
                  <p className="text-xs text-textTertiary dark:text-[#6A6A6A] mb-2">
                    {new Date(doc.createdAt).toLocaleDateString()}
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-textSecondary dark:text-[#858585]">
                      {doc.metadata?.pageCount ? `${doc.metadata.pageCount} pages` : 'Processing...'}
                    </span>
                    {doc.status === 'completed' && (
                      <span className="text-xs px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded">
                        Analyzed
                      </span>
                    )}
                    {doc.status === 'processing' && (
                      <span className="text-xs px-2 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 rounded">
                        Processing
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-backgroundSecondary dark:bg-[#252526] rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-gray-400 dark:text-gray-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-textPrimary dark:text-[#CCCCCC] mb-2">
                No documents yet
              </h2>
              <p className="text-textSecondary dark:text-[#858585]">
                Upload your first document above to get started
              </p>
            </div>
          )}
        </Container>
      </div>
    </StudyLayout>
  );
}

